import { useState } from 'react';
import { formatDate, formatDateTime, formatEuro, formatPercent } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Eyebrow, Pill, Skeleton, useToast } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { useRevertManualVolume, useSelfDisclosureVolume, type FullView, type VolumeView } from './api';
import { ManualVolumeDialog } from './ManualVolumeDialog';
import styles from './SelfDisclosurePage.module.css';

export interface VolumeTabProps {
  full: FullView;
}

function Line({
  label,
  note,
  value,
  total,
  accent,
}: {
  label: string;
  note?: string | undefined;
  value: string;
  total?: boolean | undefined;
  accent?: boolean | undefined;
}) {
  return (
    <div className={clsx(styles.calcRow, total && styles.calcRowTotal)}>
      <span className={styles.calcLabel}>
        {label}
        {note ? <span className={styles.calcNote}>{note}</span> : null}
      </span>
      <span className={clsx(styles.calcValue, accent && styles.calcValueAccent)}>{value}</span>
    </div>
  );
}

function minus(cents: number): string {
  return cents === 0 ? formatEuro(0) : `− ${formatEuro(cents)}`;
}

/** Selbstauskunft 10 „Volumenberechnung": the three calculation blocks (the sidebar below carries the used volume and the assumptions). */
export function VolumeTab({ full }: VolumeTabProps) {
  const query = useSelfDisclosureVolume(full.selfDisclosure.id, true);

  if (query.isPending) {
    return (
      <div className={styles.card} aria-busy="true">
        <Skeleton width="35%" height={20} />
        <div className={styles.skeletonRows}>
          <Skeleton width="80%" />
          <Skeleton width="70%" />
          <Skeleton width="75%" />
        </div>
      </div>
    );
  }
  if (query.isError) {
    const error = query.error;
    return (
      <Banner tone={error instanceof ApiError && error.status === 409 ? 'info' : 'danger'}>
        {errorMessage(error)}
      </Banner>
    );
  }
  const data: VolumeView = query.data;
  const rental = data.income.rental;
  const rentalLabel =
    rental.source === 'unit' && rental.unitLabel ? `Mieteinnahme ${rental.unitLabel}` : 'Mieteinnahmen';
  const rentalNote = `${formatPercent(rental.creditPct, 0)} von ${formatEuro(rental.gross)}`;

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <div className={styles.cardTitle}>Volumenberechnung</div>
          <div className={styles.cardSubtitle}>
            Berechnet aus Version {data.version} der Selbstauskunft. Die verwendeten Annahmen sind mit dieser
            Berechnung gespeichert.
          </div>
        </div>
        <Pill variant="neutral">Berechnung vom {formatDate(data.calculatedAt).slice(0, 6)}</Pill>
      </div>
      <div className={styles.calcColumns}>
        <div>
          <Eyebrow as="div" className={styles.groupLabel}>
            Anrechenbares Einkommen
          </Eyebrow>
          <Line label="Nettoeinkommen" value={formatEuro(data.income.net)} />
          <Line label="Nebentätigkeit" value={formatEuro(data.income.other)} />
          <Line label={rentalLabel} note={rentalNote} value={formatEuro(rental.credited)} />
          <Line label="Einkommen" value={formatEuro(data.income.countable)} total />
        </div>
        <div>
          <Eyebrow as="div" className={styles.groupLabel}>
            Belastungen
          </Eyebrow>
          <Line label="Lebenshaltungspauschale" value={minus(data.charges.livingExpenses)} />
          <Line label="Warmmiete" value={minus(data.charges.rent)} />
          <Line label="Leasing" value={minus(data.charges.loans + data.charges.leasing)} />
          {data.charges.alimony > 0 ? (
            <Line label="Unterhaltszahlungen" value={minus(data.charges.alimony)} />
          ) : null}
          <Line label="Frei verfügbar" value={formatEuro(data.charges.disposable)} total />
        </div>
      </div>
      <div className={styles.calcSection}>
        <Eyebrow as="div" className={styles.groupLabel}>
          Kapitaldienst
        </Eyebrow>
        <Line label="Frei verfügbar" value={formatEuro(data.service.disposable)} />
        <Line
          label="Kappungsgrenze"
          note={`${formatPercent(data.service.capPct, 0)} von ${formatEuro(data.income.countable)}`}
          value={formatEuro(data.service.capLimit)}
          accent
        />
        <Line
          label="Tragbare Rate"
          note="niedrigerer Wert"
          value={`${formatEuro(data.service.affordableInstallment)} / Monat`}
          total
        />
        <Line
          label="Annuität"
          note={`${formatPercent(data.assumptions.interestPct, 0)} Zins + ${formatPercent(data.assumptions.repaymentPct, 0)} Tilgung`}
          value={`${formatPercent(data.service.annuityPct, 2)} p. a.`}
        />
        <Line label="Rechnerisches Volumen" value={formatEuro(data.service.computedVolume)} total />
      </div>
    </div>
  );
}

/**
 * The sidebar of Selbstauskunft 10: the used volume (dark card with the manual override, „Anpassen" /
 * „Anpassung zurücknehmen") and the frozen assumptions; mounted by the page beside the calculation.
 */
export function VolumeSidebar({ full }: VolumeTabProps) {
  const { toast } = useToast();
  const query = useSelfDisclosureVolume(full.selfDisclosure.id, true);
  const revert = useRevertManualVolume(full.selfDisclosure.id);
  const [adjusting, setAdjusting] = useState(false);
  if (!query.data) return null;
  const data: VolumeView = query.data;

  return (
    <>
      <div className={styles.darkCard}>
        <Eyebrow as="div" className={styles.darkEyebrow}>
          Verwendetes Volumen
        </Eyebrow>
        <div className={styles.darkValue}>{formatEuro(data.used.volumeCents)}</div>
        {data.used.manual ? (
          <>
            <div className={styles.darkSub}>rechnerisch {formatEuro(data.service.computedVolume)}</div>
            <div className={styles.darkBadge}>
              <Pill variant="accent">manuell angepasst</Pill>
            </div>
            {data.used.reason ? <div className={styles.darkQuote}>„{data.used.reason}“</div> : null}
            <div className={styles.darkMeta}>
              {[data.used.byShort, data.used.at ? formatDateTime(data.used.at).replace(' · ', ', ') : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </>
        ) : (
          <div className={styles.darkSub}>Darlehen · rechnerisch, ohne manuelle Anpassung</div>
        )}
        {data.expiresAt && !data.used.manual ? (
          <div className={styles.darkSub}>Finanzierungsvolumen gültig bis {formatDate(data.expiresAt)}</div>
        ) : null}
        <div className={styles.darkActions}>
          {data.used.manual ? (
            <button
              type="button"
              className={styles.darkLink}
              disabled={revert.isPending}
              onClick={() =>
                revert.mutate(undefined, {
                  onSuccess: () =>
                    toast({ message: 'Anpassung zurückgenommen · das rechnerische Volumen gilt.' }),
                  onError: (error) => toast({ message: errorMessage(error) }),
                })
              }
            >
              Anpassung zurücknehmen
            </button>
          ) : null}
          <button type="button" className={styles.darkLink} onClick={() => setAdjusting(true)}>
            {data.used.manual ? 'Erneut anpassen' : 'Anpassen'}
          </button>
        </div>
      </div>
      <div className={styles.card}>
        <Eyebrow as="div">Annahmen-Snapshot · {formatDate(data.assumptions.frozenAt)}</Eyebrow>
        <div className={styles.rows}>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Lebenshaltungspauschale</span>
            <span className={styles.rowValue}>
              {formatEuro(data.assumptions.livingExpensesCents)} / Monat
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Anrechnung Mieteinnahmen</span>
            <span className={styles.rowValue}>{formatPercent(data.assumptions.rentCreditPct, 0)}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Kappung Kapitaldienst</span>
            <span className={styles.rowValue}>
              {formatPercent(data.assumptions.capPct, 0)} des Einkommens
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Kalkulationszins</span>
            <span className={styles.rowValue}>{formatPercent(data.assumptions.interestPct, 0)}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Tilgung</span>
            <span className={styles.rowValue}>{formatPercent(data.assumptions.repaymentPct, 0)}</span>
          </div>
        </div>
        <p className={styles.cardNote}>
          Berechnet am {formatDate(data.calculatedAt)} mit den Rechenannahmen, die zu diesem Zeitpunkt galten.
          Eine neue Berechnung verwendet die aktuellen Annahmen.
        </p>
      </div>
      <ManualVolumeDialog
        open={adjusting}
        selfDisclosureId={full.selfDisclosure.id}
        computedCents={data.service.computedVolume}
        currentCents={data.used.manual ? data.used.volumeCents : null}
        currentReason={data.used.reason}
        onClose={() => setAdjusting(false)}
        onSaved={() => {
          setAdjusting(false);
          toast({ message: 'Verwendetes Volumen angepasst.' });
        }}
      />
    </>
  );
}
